import mapboxgl from "mapbox-gl";
// Please keep the import order of mapbox-gl.css and theme.css same
import "mapbox-gl/dist/mapbox-gl.css";
import "./styles.module.css";
import { reaction } from "mobx";
import { useObserver } from "mobx-react";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useRef } from "react";
import { withErrorBoundary } from "react-error-boundary";
import * as configService from "./services/configService";
import CONSTANTS from "./constants";
import CustomToggleControl from "./mapboxPlugins/customToggleControl";
import LegendControl from "./mapboxPlugins/LegendControl";
import { SwitchMapTypeControl } from "./mapboxPlugins/SwitchMapTypeControl";
import { getSentryClient, withSentryErrorCapture, ErrorFallback } from "./errorBoundaryComponent";
import * as mapService from "./services/mapService";
import useStores from "./hooks/UseStores";
import Spinner from "./Spinner";
import locationPinImage from "./location-pin.png";
import redCircleImage from "./red-circle.png";
import legendImage from "./map-legend.png";
import CustomWrapperControl from "./mapboxPlugins/CustomWrapperControl";

export const { GEO_TYPE } = CONSTANTS;

const poiTradeAreaToggleControl = new CustomToggleControl({
  items: [CONSTANTS.MAP_TYPE.POI, CONSTANTS.MAP_TYPE.TRADE_AREA, CONSTANTS.MAP_TYPE.NEARBY_POIS],
  id: "poi-mode",
});

const legendControl = new LegendControl();
const customSwitchMapTypeControl = new SwitchMapTypeControl();
const customBottomRightWrapperControl = new CustomWrapperControl([customSwitchMapTypeControl, legendControl]);
const InnerGTMapboxComponent = (props) => {
  const { mapStore } = useStores();
  configService.setEnvironment(props.useProdConfig);
  mapboxgl.accessToken = configService.getConfig().MAPBOX_API_TOKEN;
  mapStore.brandId = props.brandId;
  mapStore.tenantId = props.tenantId;
  mapStore.locationGroupId = props.locationGroupId;
  mapStore.categoryId = props.categoryId;
  if (props.geo) {
    mapStore.geo = JSON.parse(JSON.stringify(props.geo));
  }
  if (props.mapPadding) {
    mapStore.mapPadding = JSON.parse(JSON.stringify(props.mapPadding));
  }
  if (props.stateDistribution) {
    mapStore.stateDistribution = JSON.parse(JSON.stringify(props.stateDistribution));
  }
  if (props.dmaDistribution) {
    mapStore.dmaDistribution = JSON.parse(JSON.stringify(props.dmaDistribution));
  }

  // show Loader property is used by parent app to  set/unset loader on map component
  // new property `showLoader` is created to capture the loader change via parent app and show/hide loader
  mapStore.showLoader = props.showLoader;
  const mapRef = useRef(null);
  useEffect(() => {
    // Binding the functions which needs to be called on click
    poiTradeAreaToggleControl.onChange = withSentryErrorCapture((item) => {
      mapService.resetPOIToggleControl(mapStore);
      const { geo } = mapStore;
      if (item === CONSTANTS.MAP_TYPE.POI) {
        if (legendControl.shown) legendControl.hide();
        mapService.switchToPOI(geo.lat, geo.lon);
        // Making sure that no nearby pois are displayed
        mapService.applyPOIFilters(mapStore.tenantId, mapStore.locationGroupId, mapStore.brandId, mapStore.categoryId, geo.geoId);
      } else if (item === CONSTANTS.MAP_TYPE.TRADE_AREA) {
        if (!legendControl.shown) legendControl.show();
        mapService.generateGeoBlocks(geo.lat, geo.lon);
      } else {
        if (legendControl.shown) legendControl.hide();
        /**
         * This is required to be able to load and query all the features in
         * poi and poi_incremental layers.
         */
        mapService.map.setPaintProperty(CONSTANTS.LAYERS.POI, "icon-opacity", 0);
        mapService.map.setPaintProperty(CONSTANTS.LAYERS.POI_INCREMENTAL, "icon-opacity", 0);
        mapService.applyPOIFilters(mapStore.tenantId, null, null, null, null);

        mapService.map.flyTo(
          {
            center: [geo.lon, geo.lat],
            zoom: CONSTANTS.NEARBY_POIS_MIN_ZOOM,
          },
          {
            name: CONSTANTS.EVENTS.NEARBY_POIS_ZOOM,
            poiId: geo.geoId,
            coordinate: [geo.lon, geo.lat],
          },
        );
      }
    });

    const onPOIIdChange = reaction(
      () => mapStore.geo,
      withSentryErrorCapture((geo) => {
        if (geo.geoType !== GEO_TYPE.POI) {
          mapService.setActivePOIFeature(null);
          if (poiTradeAreaToggleControl.map) mapService.map.removeControl(poiTradeAreaToggleControl);
          if (!legendControl.shown) legendControl.show();
          mapService.hideLayers([
            CONSTANTS.LAYERS.POI,
            CONSTANTS.LAYERS.POI_INCREMENTAL,
            CONSTANTS.LAYERS.IN_STORE,
            CONSTANTS.LAYERS.IN_STORE_INCREMENTAL,
            CONSTANTS.LAYERS.IN_STORE_INCREMENTAL_BORDER,
            CONSTANTS.LAYERS.IN_STORE_BORDER,
            CONSTANTS.LAYERS.ON_LOT,
            CONSTANTS.LAYERS.ON_LOT_INCREMENTAL,
            CONSTANTS.LAYERS.ON_LOT_INCREMENTAL_BORDER,
            CONSTANTS.LAYERS.ON_LOT_BORDER,
          ]);
          mapService.resetPOIToggleControl(mapStore);
          mapService.setLayersVisibility(Object.values(mapService.poiPartitionedLayers), CONSTANTS.LAYOUT_PROPERTIES.VISIBLE);
          [CONSTANTS.LAYERS.POI, CONSTANTS.LAYERS.POI_INCREMENTAL].map((layer) =>
            mapService.map.setLayoutProperty(layer, "icon-image", "custom-marker"),
          );
          return;
        }
        // hide partitioned layers
        mapService.setLayersVisibility(Object.values(mapService.poiPartitionedLayers), CONSTANTS.LAYOUT_PROPERTIES.NONE);
        mapService.setLayersVisibility(
          [
            CONSTANTS.LAYERS.POI,
            CONSTANTS.LAYERS.POI_INCREMENTAL,
            CONSTANTS.LAYERS.IN_STORE,
            CONSTANTS.LAYERS.IN_STORE_INCREMENTAL,
            CONSTANTS.LAYERS.IN_STORE_BORDER,
            CONSTANTS.LAYERS.IN_STORE_INCREMENTAL_BORDER,
            CONSTANTS.LAYERS.ON_LOT,
            CONSTANTS.LAYERS.ON_LOT_INCREMENTAL,
            CONSTANTS.LAYERS.ON_LOT_BORDER,
            CONSTANTS.LAYERS.ON_LOT_INCREMENTAL_BORDER,
          ],
          CONSTANTS.LAYOUT_PROPERTIES.VISIBLE,
        );

        if (!poiTradeAreaToggleControl.map) {
          mapService.map.addControl(poiTradeAreaToggleControl);
        }
        if (legendControl.shown) legendControl.hide();
        poiTradeAreaToggleControl.changeActiveItem(CONSTANTS.MAP_TYPE.POI);
        // disable trade area button
        poiTradeAreaToggleControl.setItemState(CONSTANTS.MAP_TYPE.TRADE_AREA, true);

        // Hiding the layers as by default `POI View` is selected
        mapService.hideLayers([
          CONSTANTS.LAYERS.GEO_BLOCKS,
          CONSTANTS.LAYERS.GEO_BLOCKS_BORDER,
          CONSTANTS.LAYERS.STATE_HIGHLIGHTED,
          CONSTANTS.LAYERS.DMA_HIGHLIGHTED,
          // Hiding these layers so that we can make them visible conditionally later.
          CONSTANTS.LAYERS.IN_STORE,
          CONSTANTS.LAYERS.IN_STORE_INCREMENTAL,
          CONSTANTS.LAYERS.IN_STORE_BORDER,
          CONSTANTS.LAYERS.IN_STORE_INCREMENTAL_BORDER,
          CONSTANTS.LAYERS.ON_LOT,
          CONSTANTS.LAYERS.ON_LOT_INCREMENTAL,
          CONSTANTS.LAYERS.ON_LOT_BORDER,
          CONSTANTS.LAYERS.ON_LOT_INCREMENTAL_BORDER,
        ]);

        // Clearing any selected state/dma
        mapStore.stateId = null;
        mapStore.dmaId = null;
        mapService.setLayersVisibility(
          [CONSTANTS.LAYERS.STATE, CONSTANTS.LAYERS.STATE_BORDER, CONSTANTS.LAYERS.DMA, CONSTANTS.LAYERS.DMA_BORDER],
          CONSTANTS.LAYOUT_PROPERTIES.NONE,
        );
        mapService.applyPOIFilters(mapStore.tenantId, mapStore.locationGroupId, mapStore.brandId, mapStore.categoryId, geo.geoId);
        mapService.handleActivePOIChange(geo.geoId, geo.lat, geo.lon);
      }),
      {
        equals: (prev, current) => {
          if (!prev || !current) return false;
          // Track POI Mode IN/Out
          const POIModeIn = prev.geoType !== GEO_TYPE.POI && current.geoType === GEO_TYPE.POI;
          const POIModeOut = prev.geoType === GEO_TYPE.POI && current.geoType !== GEO_TYPE.POI;

          if (POIModeIn || POIModeOut) return false;

          if (prev.geoType === GEO_TYPE.POI && current.geoType === GEO_TYPE.POI) {
            return prev.geoId === current.geoId;
          }

          return true;
        },
      },
    );

    return () => {
      onPOIIdChange();
    };
  }, []);

  /**
   * Function which is used to validate whether the clicked Poi is selected poi before calling `onGeoSelection`
   * @param {*} feature Value of the clicked Poi Object
   */
  function onPOIClick(feature) {
    const clickedPoi = feature.properties.id;
    if (mapService.IsPoiEqualsActivePoi(clickedPoi)) {
      console.warn("Click disabled on the selected poi");
      return;
    }
    props.onGeoSelection({
      geoType: GEO_TYPE.POI,
      geoId: clickedPoi,
    });
  }

  /**
   * Function which is used to setup all the required reactions
   * @returns {*} Objects of reactions in an array */
  function mapReactions() {
    // Reaction on state Distribution change
    const stateDistributionReaction = reaction(
      () => mapStore.stateDistribution,
      withSentryErrorCapture((stateDistribution) => {
        if (!legendControl.shown) legendControl.show();
        mapService.updateSourceVisitsAndLocations(stateDistribution, CONSTANTS.SOURCES.STATE, mapStore);
        mapService.applyPOIFilters(mapStore.tenantId, mapStore.locationGroupId, mapStore.brandId, mapStore.categoryId);
      }),
      {
        equals: (prev, current) => {
          /* Handling null case */ if (!prev) return false;
          if (!current || !current.geos.length) return true;
          return prev.requestId === current.requestId;
        },
      },
    );

    // Reaction on dma Distribution change
    const dmaDistributionReaction = reaction(
      () => mapStore.dmaDistribution,
      withSentryErrorCapture((dmaDistribution) => {
        if (!legendControl.shown) legendControl.show();
        mapService.updateSourceVisitsAndLocations(dmaDistribution, CONSTANTS.SOURCES.DMA, mapStore);
        mapService.applyPOIFilters(mapStore.tenantId, mapStore.locationGroupId, mapStore.brandId, mapStore.categoryId);
      }),
      {
        equals: (prev, current) => {
          /* Handling null case */ if (!prev) return false;
          if (!current || !current.geos.length) return true;
          return prev.requestId === current.requestId;
        },
      },
    );

    // Reaction on map padding change
    const mapPaddingReaction = reaction(
      () => mapStore.mapPadding,
      withSentryErrorCapture((mapPaddingObject) => {
        mapService.setMapPadding(mapPaddingObject);
      }),
      {
        equals: (prev, current) => {
          return !(
            prev.left !== current.left ||
            prev.right !== current.right ||
            prev.top !== current.top ||
            prev.bottom !== current.bottom
          );
        },
      },
    );

    // Reaction on geo change
    const geoReaction = reaction(
      () => mapStore.geo,
      withSentryErrorCapture((geo) => {
        switch (geo.geoType) {
          case GEO_TYPE.STATE:
            if (mapStore.stateId === geo.geoId) {
              console.warn("State with ID:", geo.geoId, "is already selected");
              return;
            }
            mapService.zoomAndSelectState(geo.geoId, mapStore);
            break;
          case GEO_TYPE.DMA:
            if (mapStore.dmaId === geo.geoId) {
              console.warn("DMA with ID:", geo.geoId, "is already selected");
              return;
            }
            mapService.zoomAndSelectDMA(geo.geoId, mapStore);
            break;
          default:
            // De-selecting any State or DMA
            mapService.hideLayers([CONSTANTS.LAYERS.DMA_HIGHLIGHTED, CONSTANTS.LAYERS.STATE_HIGHLIGHTED]);
            mapStore.stateId = null;
            mapStore.dmaId = null;
            // Resetting zoom to national level
            mapService.map.flyTo({
              center: CONSTANTS.LAYERS_CONFIG.CENTER,
              zoom: CONSTANTS.LAYERS_CONFIG.DEFAULT_ZOOM,
            });
            break;
        }
        mapService.applyPOIFilters(mapStore.tenantId, mapStore.locationGroupId, mapStore.brandId, mapStore.categoryId);
        mapService.setLayersVisibility(
          [CONSTANTS.LAYERS.STATE, CONSTANTS.LAYERS.STATE_BORDER, CONSTANTS.LAYERS.DMA, CONSTANTS.LAYERS.DMA_BORDER],
          CONSTANTS.LAYOUT_PROPERTIES.VISIBLE,
        );
      }),
      {
        equals: (prev, current) => {
          if (current.geoType === GEO_TYPE.POI) return true;
          if (!prev) return false;
          return prev.geoType === current.geoType && prev.geoId === current.geoId;
        },
      },
    );

    // Reaction on stateId change
    const stateReaction = reaction(
      () => mapStore.stateId,
      withSentryErrorCapture((stateId) => {
        props.onGeoSelection({
          geoType: GEO_TYPE.STATE,
          geoId: stateId,
        });
      }),
      {
        equals: (prev, current) => {
          if (!current) return true;
        },
      },
    );

    // Reaction on dmaId change
    const dmaReaction = reaction(
      () => mapStore.dmaId,
      withSentryErrorCapture((dmaId) => {
        props.onGeoSelection({
          geoType: GEO_TYPE.DMA,
          geoId: dmaId,
        });
      }),
      {
        equals: (prev, current) => {
          if (!current) return true;
        },
      },
    );

    return [geoReaction, stateReaction, dmaReaction, stateDistributionReaction, dmaDistributionReaction, mapPaddingReaction];
  }

  /**
   * Function which is used to dispose off all the setup reactions
   * @param {*} disposables Array containing all the reactions to be disposed off */
  function disposeReactions(disposables) {
    // Disposing reactions
    disposables.forEach((reactionToDispose) => {
      reactionToDispose();
    });
  }

  useEffect(() => {
    let disposables = [];
    if (!mapboxgl.supported()) {
      throw new Error("MapboxGL is not supported! Please update your browser.");
    }

    mapService.setMap(
      new mapboxgl.Map({
        container: mapRef.current,
        style: configService.getConfig().MAPBOX_STYLE_URL,
        center: CONSTANTS.LAYERS_CONFIG.CENTER,
        zoom: CONSTANTS.LAYERS_CONFIG.DEFAULT_ZOOM,
      }),
    );
    mapService.map.on("movestart", () => {
      // reset the POI id filters so that all pois present in layers can be queried
      // this should only be active for home screen
      if (!poiTradeAreaToggleControl.activeItem) {
        mapService.applyPOIFilters(mapStore.tenantId, mapStore.locationGroupId, mapStore.brandId, mapStore.categoryId);
      }
    });

    Object.values(mapService.poiPartitionedLayers).map((layer) => {
      // Called when user moves cursor over poi present in poi-snapshot layer
      mapService.map.on("mouseenter", layer, function poiLayerMouseEnter(e) {
        mapService.setCursor(CONSTANTS.CURSOR_TYPES.POINTER);
        const feature = e.features[0];
        const poiId = feature.properties.id;
        mapService.setActiveHoverPoiId(poiId);
        mapService.updatePoiLayerFeature(feature, CONSTANTS.LAYERS.POI, props.authToken);
      });

      mapService.map.on("mouseleave", layer, function poiLayerMouseLeave(e) {
        mapService.setCursor(CONSTANTS.CURSOR_TYPES.BLANK);
        mapService.setActiveHoverPoiId(null);
        mapService.popup.remove();
      });

      return layer;
    });

    // Called when user moves cursor over poi present in poi-snapshot layer
    mapService.map.on("mouseenter", CONSTANTS.LAYERS.POI, function poiLayerMouseEnter(e) {
      const feature = e.features[0];
      if (mapService.queryRenderedFeature(e.point, CONSTANTS.LAYERS.POI_INCREMENTAL)) {
        return;
      }
      const poiId = feature.properties.id;
      if (!mapService.IsPoiEqualsActivePoi(poiId)) {
        mapService.setCursor(CONSTANTS.CURSOR_TYPES.POINTER);
      }
      // Used to validate that pointer is still over poi when api response is retrieved
      mapService.setActiveHoverPoiId(poiId);
      mapService.updatePoiLayerFeature(feature, CONSTANTS.LAYERS.POI, props.authToken);
    });

    // Called when user moves cursor away from poi present in poi-snapshot layer
    mapService.map.on("mouseleave", CONSTANTS.LAYERS.POI, function poiLayerMouseLeave(e) {
      mapService.setCursor(CONSTANTS.CURSOR_TYPES.BLANK);
      mapService.setActiveHoverPoiId(null);
      mapService.popup.remove();
    });

    // Called when user moves cursor over poi present in poi-incremental layer
    mapService.map.on("mouseenter", CONSTANTS.LAYERS.POI_INCREMENTAL, function poiIncrementalLayerMouseEnter(e) {
      const feature = e.features[0];
      const poiId = feature.properties.id;
      if (!mapService.IsPoiEqualsActivePoi(poiId)) {
        mapService.setCursor(CONSTANTS.CURSOR_TYPES.POINTER);
      }
      // Used to validate that pointer is still over poi when api response is retrieved
      mapService.setActiveHoverPoiId(poiId);
      mapService.updatePoiLayerFeature(feature, CONSTANTS.LAYERS.POI_INCREMENTAL, props.authToken);
    });

    // Called when user moves cursor away from poi present in poi-incremental layer
    mapService.map.on("mouseleave", CONSTANTS.LAYERS.POI_INCREMENTAL, function poiIncrementalLayerMouseLeave(e) {
      mapService.setCursor(CONSTANTS.CURSOR_TYPES.BLANK);
      mapService.setActiveHoverPoiId(null);
      mapService.popup.remove();
    });

    mapService.map.on(
      "moveend",
      withSentryErrorCapture((e) => {
        switch (e.name) {
          case CONSTANTS.EVENTS.POI_SELECTED:
            mapService.map.once("idle", () => {
              mapService.fitPOIFeatureBounds(e.poiId);
              // this function is required to be called here, as it needs activePOIFeature to be present,
              // which is set in above function
              mapService.loadGeoblockData(poiTradeAreaToggleControl, props.authToken);
            });
            break;
          case CONSTANTS.EVENTS.SHOW_GEOBLOCKS:
            mapService.map.once("idle", () => mapService.computeGeoBlocksBounds(e.filter));
            break;
          case CONSTANTS.EVENTS.ZOOM_TO_STATE:
            mapService.map.once("idle", () => mapService.zoomAndSelectState(e.stateId, mapStore));
            break;
          case CONSTANTS.EVENTS.NEARBY_POIS_ZOOM:
            // handling nearby pois zoom separately, ensure that no extra pois are loaded on
            // subsequent zoom/pan action on locations page
            mapService.map.once("idle", () => mapService.showPoisBasedOnFilter(mapStore));
            break;
          default: {
            // fetch/dedup pois in the view port for zoom to poi feature on home page
            if (!poiTradeAreaToggleControl.activeItem) {
              mapService.map.once("idle", () => mapService.showPoisBasedOnFilter(mapStore));
            }
          }
        }
      }),
    );

    mapService.map.addControl(customBottomRightWrapperControl);
    mapService.map.addControl(
      new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true,
      }),
      "bottom-right",
    );

    mapService.setCursor(CONSTANTS.CURSOR_TYPES.GRAB);

    mapService.map.on(
      "mousemove",
      CONSTANTS.LAYERS.STATE,
      withSentryErrorCapture((e) => {
        mapService.setCursor(CONSTANTS.CURSOR_TYPES.POINTER);

        // If state under mouse has no locations, change the cursor
        let feature = null;
        if ((feature = mapService.queryRenderedFeature(e.point, CONSTANTS.LAYERS.STATE))) {
          const locationCount = mapService.map.getFeatureState({
            source: CONSTANTS.SOURCES.STATE,
            sourceLayer: CONSTANTS.LAYERS.STATE,
            id: feature.properties.id,
          }).location;
          if (!locationCount) {
            mapService.setCursor(CONSTANTS.CURSOR_TYPES.NOT_ALLOWED);
          }
        }

        mapService.displayPopup(e.point, e.lngLat, CONSTANTS.LAYERS.STATE);
      }),
    );

    mapService.map.on(
      "mousemove",
      CONSTANTS.LAYERS.DMA,
      withSentryErrorCapture((e) => {
        // Will display popup containing DMA info only when poi is not visible and selected on that same zoom level
        if (!mapService.getActiveHoverPoiId()) {
          mapService.setCursor(CONSTANTS.CURSOR_TYPES.POINTER);
          // If dma under mouse has no locations, change the cursor
          let feature = null;
          if ((feature = mapService.queryRenderedFeature(e.point, CONSTANTS.LAYERS.DMA))) {
            const locationCount = mapService.map.getFeatureState({
              source: CONSTANTS.SOURCES.DMA,
              sourceLayer: CONSTANTS.LAYERS.DMA,
              id: feature.properties.id,
            }).location;
            if (!locationCount) {
              mapService.setCursor(CONSTANTS.CURSOR_TYPES.NOT_ALLOWED);
            }
          }

          mapService.displayPopup(e.point, e.lngLat, CONSTANTS.LAYERS.DMA);
        }
      }),
    );

    mapService.map.on(
      "mouseleave",
      CONSTANTS.LAYERS.STATE,
      withSentryErrorCapture(() => {
        mapService.setCursor(CONSTANTS.CURSOR_TYPES.GRAB);
        mapService.popup.remove();
      }),
    );

    mapService.map.on(
      "mouseleave",
      CONSTANTS.LAYERS.DMA,
      withSentryErrorCapture(() => {
        mapService.setCursor(CONSTANTS.CURSOR_TYPES.GRAB);
        mapService.popup.remove();
      }),
    );

    mapService.map.on(
      "style.load",
      withSentryErrorCapture(() => {
        [
          {
            id: "custom-marker",
            url: locationPinImage,
          },
          {
            id: "circle",
            url: redCircleImage,
          },
          {
            id: "map-legend",
            url: legendImage,
          },
        ].forEach((icon) => {
          const image = new Image();
          // after bundling, image data is received in Symbol object. The base64 representation
          // of image is present in `default` property of that object
          image.src = typeof icon.url === "string" ? icon.url : icon.url.default;
          image.onload = () => {
            mapService.map.addImage(icon.id, image);
          };
          image.onerror = (error) => {
            console.error("images failed to load:", error);
          };
        });

        // Load the sources
        Object.values(CONSTANTS.SOURCES).forEach((source) => {
          mapService.map.addSource(source, {
            type: "vector",
            url: configService.getConfig().MAPBOX_ACCOUNT + source,
          });
        });

        // // Load the partitioned sources
        Object.values(mapService.poiPartitionedLayers).forEach((source) => {
          mapService.map.addSource(source, {
            type: "vector",
            url: configService.getConfig().MAPBOX_ACCOUNT + source,
          });
        });

        Object.values(mapService.poiPartitionedLayers).forEach((source) => {
          mapService.map.addLayer(
            {
              id: source,
              type: "symbol",
              source,
              layout: {
                "icon-image": "custom-marker",
                "icon-allow-overlap": true,
                "icon-size": 0.5,
              },
              "source-layer": source,
            },
            "aerialway",
          );
        });

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.POI,
            type: "symbol",
            source: CONSTANTS.SOURCES.POI,
            layout: {
              "icon-image": "circle",
              "icon-allow-overlap": true,
              "icon-size": 0.5,
            },
            "source-layer": CONSTANTS.SOURCES.POI,
            minzoom: CONSTANTS.LAYERS_CONFIG.POI_MIN_ZOOM,
          },
          "aerialway",
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.POI_INCREMENTAL,
            type: "symbol",
            source: CONSTANTS.SOURCES.POI_INCREMENTAL,
            layout: {
              "icon-image": "circle",
              "icon-allow-overlap": true,
              "icon-size": 0.5,
            },
            "source-layer": CONSTANTS.SOURCES.POI_INCREMENTAL,
            minzoom: CONSTANTS.LAYERS_CONFIG.POI_MIN_ZOOM,
          },
          "aerialway",
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.IN_STORE_BORDER,
            type: "line",
            source: CONSTANTS.SOURCES.IN_STORE,
            layout: {},
            paint: {
              "line-color": CONSTANTS.LAYERS_CONFIG.IN_STORE_BORDER.LINE_COLOR,
              "line-opacity": CONSTANTS.LAYERS_CONFIG.IN_STORE_BORDER.LINE_OPACITY,
            },
            "source-layer": CONSTANTS.SOURCES.IN_STORE,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.POI,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.IN_STORE_INCREMENTAL_BORDER,
            type: "line",
            source: CONSTANTS.SOURCES.IN_STORE_INCREMENTAL,
            layout: {},
            paint: {
              "line-color": CONSTANTS.LAYERS_CONFIG.IN_STORE_BORDER.LINE_COLOR,
              "line-opacity": CONSTANTS.LAYERS_CONFIG.IN_STORE_BORDER.LINE_OPACITY,
            },
            "source-layer": CONSTANTS.SOURCES.IN_STORE_INCREMENTAL,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.POI,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.ON_LOT_BORDER,
            type: "line",
            source: CONSTANTS.SOURCES.ON_LOT,
            layout: {},
            paint: {
              "line-color": CONSTANTS.LAYERS_CONFIG.ON_LOT_BORDER.LINE_COLOR,
              "line-opacity": CONSTANTS.LAYERS_CONFIG.ON_LOT_BORDER.LINE_OPACITY,
            },
            "source-layer": CONSTANTS.LAYERS.ON_LOT,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.IN_STORE_BORDER,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.ON_LOT_INCREMENTAL_BORDER,
            type: "line",
            source: CONSTANTS.SOURCES.ON_LOT_INCREMENTAL,
            layout: {},
            paint: {
              "line-color": CONSTANTS.LAYERS_CONFIG.ON_LOT_BORDER.LINE_COLOR,
              "line-opacity": CONSTANTS.LAYERS_CONFIG.ON_LOT_BORDER.LINE_OPACITY,
            },
            "source-layer": CONSTANTS.LAYERS.ON_LOT_INCREMENTAL,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.IN_STORE_BORDER,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.GEO_BLOCKS_BORDER,
            type: "line",
            source: CONSTANTS.SOURCES.GEO_BLOCKS,
            layout: {},
            paint: {
              "line-color": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_COLOR,
              "line-width": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_WIDTH,
              "line-opacity": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_OPACITY,
            },
            "source-layer": CONSTANTS.LAYERS.GEO_BLOCKS,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.IN_STORE_BORDER,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.IN_STORE,
            type: "fill",
            source: CONSTANTS.SOURCES.IN_STORE,
            layout: {},
            paint: {
              "fill-color": CONSTANTS.LAYERS_CONFIG.IN_STORE.FILL_COLOR,
              "fill-opacity": CONSTANTS.LAYERS_CONFIG.IN_STORE.FILL_OPACITY,
            },
            "source-layer": CONSTANTS.SOURCES.IN_STORE,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.GEO_BLOCKS_BORDER,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.IN_STORE_INCREMENTAL,
            type: "fill",
            source: CONSTANTS.SOURCES.IN_STORE_INCREMENTAL,
            layout: {},
            paint: {
              "fill-color": CONSTANTS.LAYERS_CONFIG.IN_STORE.FILL_COLOR,
              "fill-opacity": CONSTANTS.LAYERS_CONFIG.IN_STORE.FILL_OPACITY,
            },
            "source-layer": CONSTANTS.SOURCES.IN_STORE_INCREMENTAL,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.GEO_BLOCKS_BORDER,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.ON_LOT,
            type: "fill",
            source: CONSTANTS.LAYERS.ON_LOT,
            layout: {},
            paint: {
              "fill-color": CONSTANTS.LAYERS_CONFIG.ON_LOT.FILL_COLOR,
              "fill-opacity": CONSTANTS.LAYERS_CONFIG.ON_LOT.FILL_OPACITY,
            },
            "source-layer": CONSTANTS.SOURCES.ON_LOT,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.IN_STORE,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.ON_LOT_INCREMENTAL,
            type: "fill",
            source: CONSTANTS.LAYERS.ON_LOT_INCREMENTAL,
            layout: {},
            paint: {
              "fill-color": CONSTANTS.LAYERS_CONFIG.ON_LOT.FILL_COLOR,
              "fill-opacity": CONSTANTS.LAYERS_CONFIG.ON_LOT.FILL_OPACITY,
            },
            "source-layer": CONSTANTS.SOURCES.ON_LOT_INCREMENTAL,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.IN_STORE,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.GEO_BLOCKS,
            type: "fill",
            source: CONSTANTS.LAYERS.GEO_BLOCKS,
            layout: {},
            paint: {
              "fill-color": mapService.GEOBLOCKS_FILL_COLOR,
              "fill-opacity": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_OPACITY,
            },
            "source-layer": CONSTANTS.SOURCES.GEO_BLOCKS,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.ON_LOT,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.DMA_HIGHLIGHTED,
            type: "line",
            source: CONSTANTS.SOURCES.DMA,
            layout: {},
            paint: {
              "line-color": CONSTANTS.LAYERS_CONFIG.HIGHLIGHTED_LAYER.LINE_COLOR,
              "line-width": CONSTANTS.LAYERS_CONFIG.HIGHLIGHTED_LAYER.LINE_WIDTH,
              "line-opacity": CONSTANTS.LAYERS_CONFIG.HIGHLIGHTED_LAYER.LINE_OPACITY,
            },
            "source-layer": "dma",
            minzoom: CONSTANTS.LAYERS_CONFIG.DMA_MIN_ZOOM,
            maxzoom: CONSTANTS.LAYERS_CONFIG.DMA_MAX_ZOOM,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.GEO_BLOCKS,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.STATE_HIGHLIGHTED,
            type: "line",
            source: CONSTANTS.SOURCES.STATE,
            layout: {},
            paint: {
              "line-color": CONSTANTS.LAYERS_CONFIG.HIGHLIGHTED_LAYER.LINE_COLOR,
              "line-width": CONSTANTS.LAYERS_CONFIG.HIGHLIGHTED_LAYER.LINE_WIDTH,
              "line-opacity": CONSTANTS.LAYERS_CONFIG.HIGHLIGHTED_LAYER.LINE_OPACITY,
            },
            "source-layer": CONSTANTS.SOURCES.STATE,
            filter: ["in", "id", ""],
          },
          CONSTANTS.LAYERS.DMA_HIGHLIGHTED,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.DMA_BORDER,
            type: "line",
            source: CONSTANTS.SOURCES.DMA,
            layout: {},
            paint: {
              "line-color": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_COLOR,
              "line-width": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_WIDTH,
              "line-opacity": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_OPACITY,
            },
            "source-layer": CONSTANTS.SOURCES.DMA,
            minzoom: CONSTANTS.LAYERS_CONFIG.DMA_MIN_ZOOM,
            maxzoom: CONSTANTS.LAYERS_CONFIG.DMA_MAX_ZOOM,
          },
          CONSTANTS.LAYERS.STATE_HIGHLIGHTED,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.STATE_BORDER,
            type: "line",
            source: CONSTANTS.SOURCES.STATE,
            layout: {},
            paint: {
              "line-color": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_COLOR,
              "line-width": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_WIDTH,
              "line-opacity": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_OPACITY,
            },
            "source-layer": CONSTANTS.SOURCES.STATE,
            maxzoom: CONSTANTS.LAYERS_CONFIG.STATE_MAX_ZOOM,
          },
          CONSTANTS.LAYERS.DMA_BORDER,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.DMA,
            type: "fill",
            source: CONSTANTS.SOURCES.DMA,
            layout: {},
            paint: {
              "fill-color": mapService.STATE_DMA_FILL_COLOR,
              "fill-opacity": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_OPACITY,
            },
            "source-layer": "dma",
            minzoom: CONSTANTS.LAYERS_CONFIG.DMA_MIN_ZOOM,
            maxzoom: CONSTANTS.LAYERS_CONFIG.DMA_MAX_ZOOM,
          },
          CONSTANTS.LAYERS.STATE_BORDER,
        );

        mapService.map.addLayer(
          {
            id: CONSTANTS.LAYERS.STATE,
            type: "fill",
            source: CONSTANTS.SOURCES.STATE,
            layout: {},
            paint: {
              "fill-color": mapService.STATE_DMA_FILL_COLOR,
              "fill-opacity": CONSTANTS.LAYERS_CONFIG.BORDER_LAYER.LINE_OPACITY,
            },
            "source-layer": "state",
            maxzoom: CONSTANTS.LAYERS_CONFIG.STATE_MAX_ZOOM,
          },
          CONSTANTS.LAYERS.DMA,
        );

        mapService.setLayersVisibility(
          [
            CONSTANTS.LAYERS.STATE,
            CONSTANTS.LAYERS.STATE_BORDER,
            CONSTANTS.LAYERS.DMA,
            CONSTANTS.LAYERS.DMA_BORDER,
            CONSTANTS.LAYERS.POI,
            CONSTANTS.LAYERS.POI_INCREMENTAL,
          ],
          CONSTANTS.LAYOUT_PROPERTIES.NONE,
        );

        mapService.map.once(
          "idle",
          withSentryErrorCapture((e) => {
            mapService.initMap(mapStore);
            mapService.setMapPadding(mapStore.mapPadding);
            disposables = mapReactions();
          }),
        );
      }),
    );

    mapService.map.on(
      "click",
      withSentryErrorCapture((e) => {
        const bbox = [
          [e.point.x, e.point.y],
          [e.point.x, e.point.y],
        ];

        let feature = null;

        if ((feature = mapService.queryRenderedFeature(bbox, Object.values(mapService.poiPartitionedLayers)))) {
          onPOIClick(feature);
        } else if ((feature = mapService.queryRenderedFeature(bbox, CONSTANTS.LAYERS.POI_INCREMENTAL))) {
          onPOIClick(feature);
        } else if ((feature = mapService.queryRenderedFeature(bbox, CONSTANTS.LAYERS.POI))) {
          onPOIClick(feature);
        } else if ((feature = mapService.queryRenderedFeature(bbox, CONSTANTS.LAYERS.STATE))) {
          // Fetching the location count of the selected state
          const locationCount = mapService.map.getFeatureState({
            source: CONSTANTS.SOURCES.STATE,
            sourceLayer: CONSTANTS.LAYERS.STATE,
            id: feature.properties.id,
          }).location;

          // Zooming and selecting only if state has locations
          if (locationCount) {
            mapService.zoomAndSelectState(feature.properties.id, mapStore);
          }
        } else if ((feature = mapService.queryRenderedFeature(bbox, CONSTANTS.LAYERS.DMA))) {
          // Fetching the location count of the selected DMA
          const locationCount = mapService.map.getFeatureState({
            source: CONSTANTS.SOURCES.DMA,
            sourceLayer: CONSTANTS.LAYERS.DMA,
            id: feature.properties.id,
          }).location;

          // Zooming and selecting only if DMA has locations
          if (locationCount) {
            mapService.zoomAndSelectDMA(feature.properties.id, mapStore);
          }
        }
      }),
    );
    return () => disposeReactions(disposables);
  }, []);

  return useObserver(() => (
    <Fragment>
      <Spinner size='medium' active={mapStore.showLoader} />
      <div id='map' ref={mapRef} />
    </Fragment>
  ));
};

InnerGTMapboxComponent.propTypes = {
  geo: PropTypes.exact({
    geoType: PropTypes.oneOf([GEO_TYPE.COUNTRY, GEO_TYPE.STATE, GEO_TYPE.DMA, GEO_TYPE.POI]).isRequired,
    geoId: PropTypes.number.isRequired,
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),

  poiLatLon: (props, propName, componentName) => {
    let msg = `${componentName}: missing `;
    const geoProp = props.geo;
    if (geoProp && geoProp.geoType === GEO_TYPE.POI && (geoProp.lat == null || geoProp.lon == null)) {
      if (props.geo.lat == null) {
        msg += "lat";
      } else if (props.geo.lon == null) {
        msg += "lon";
      }
      return new Error(`${msg} attribute for geo prop with geoType - ${GEO_TYPE.POI}`);
    }
  },

  brandId: PropTypes.number,

  categoryId: PropTypes.string,

  onGeoSelection: PropTypes.func,

  stateDistribution: PropTypes.exact({
    requestId: PropTypes.number.isRequired,
    geos: PropTypes.array.isRequired,
  }),

  dmaDistribution: PropTypes.exact({
    requestId: PropTypes.number.isRequired,
    geos: PropTypes.array.isRequired,
  }),

  showLoader: PropTypes.bool,

  tenantId: PropTypes.number,

  locationGroupId: PropTypes.number,

  authToken: PropTypes.string.isRequired,

  useProdConfig: PropTypes.bool.isRequired,

  mapPadding: PropTypes.exact({
    left: PropTypes.number,
    right: PropTypes.number,
    top: PropTypes.number,
    bottom: PropTypes.number,
  }),
};

export const GTMapboxComponent = withErrorBoundary(InnerGTMapboxComponent, {
  FallbackComponent: ErrorFallback,
  onError: (error, componentStack) => {
    getSentryClient().captureException(error);
  },
});
